import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Moon, Sun, Menu, X, Sparkles, Globe } from 'lucide-react';
import { useApp } from '../context/AppContext';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';

const Navbar = () => {
  const { t, theme, toggleTheme, lang, toggleLang } = useApp();
  const { user } = useAuth();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const links = [
    { label: t.nav.chat, to: '/chat' },
    { label: t.nav.agents, to: '/agents' },
    { label: t.nav.build, to: '/build' },
    { label: t.nav.pricing, to: '/#pricing' }
  ];

  const goTo = (to) => {
    setOpen(false);
    if (to.startsWith('/#')) {
      if (location.pathname !== '/') {
        navigate('/');
        setTimeout(() => document.querySelector(to.slice(1))?.scrollIntoView({ behavior: 'smooth' }), 300);
      } else {
        document.querySelector(to.slice(1))?.scrollIntoView({ behavior: 'smooth' });
      }
    } else {
      navigate(to);
    }
  };

  return (
    <header className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${scrolled ? 'bg-background/80 backdrop-blur-lg border-b border-border' : 'bg-transparent'}`}>
      <nav className="max-w-7xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2 group" onClick={() => setOpen(false)}>
          <span className="h-9 w-9 rounded-xl bg-primary flex items-center justify-center shadow-sm transition-transform group-hover:scale-105">
            <Sparkles className="h-5 w-5 text-primary-foreground" />
          </span>
          <span className="font-display text-xl font-bold tracking-tight">Aurra<span className="text-primary">.</span></span>
        </Link>

        <div className="hidden md:flex items-center gap-1">
          {links.map((l) => (
            <button key={l.to} onClick={() => goTo(l.to)} className="px-4 py-2 text-sm font-medium text-muted-foreground hover:text-foreground rounded-lg hover:bg-accent transition-colors">
              {l.label}
            </button>
          ))}
        </div>

        <div className="flex items-center gap-1.5">
          <button onClick={toggleLang} className="h-9 px-2.5 rounded-lg hover:bg-accent transition-colors flex items-center gap-1.5 text-sm font-semibold text-muted-foreground hover:text-foreground" aria-label="language">
            <Globe className="h-4 w-4" /> {lang.toUpperCase()}
          </button>
          <button onClick={toggleTheme} className="h-9 w-9 rounded-lg hover:bg-accent transition-colors flex items-center justify-center text-muted-foreground hover:text-foreground" aria-label="theme">
            {theme === 'dark' ? <Sun className="h-[18px] w-[18px]" /> : <Moon className="h-[18px] w-[18px]" />}
          </button>
          {user ? (
            <Button onClick={() => navigate('/chat')} data-testid="nav-launch-btn" className="hidden sm:inline-flex ml-1 rounded-lg font-semibold">
              {t.nav.launch}
            </Button>
          ) : (
            <>
              <Button onClick={() => navigate('/login')} variant="ghost" data-testid="nav-login-btn" className="hidden sm:inline-flex ml-1 rounded-lg font-semibold">{t.nav.login}</Button>
              <Button onClick={() => navigate('/login?mode=register')} data-testid="nav-signup-btn" className="hidden sm:inline-flex rounded-lg font-semibold">{t.nav.signup}</Button>
            </>
          )}
          <button onClick={() => setOpen((o) => !o)} className="md:hidden h-9 w-9 rounded-lg hover:bg-accent flex items-center justify-center" aria-label="menu">
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </nav>

      {open && (
        <div className="md:hidden border-t border-border bg-background px-5 py-4 space-y-1 animate-fade-in">
          {links.map((l) => (
            <button key={l.to} onClick={() => goTo(l.to)} className="block w-full text-left px-4 py-3 rounded-lg text-sm font-medium hover:bg-accent">
              {l.label}
            </button>
          ))}
          <Button onClick={() => { setOpen(false); navigate(user ? '/chat' : '/login'); }} className="w-full mt-2 rounded-lg font-semibold">{user ? t.nav.launch : t.nav.login}</Button>
        </div>
      )}
    </header>
  );
};

export default Navbar;
